"use client";

import { ErrorCard } from "@/components/ui/shared/ErrorCard";
import { useErrorAutoRetry } from "@/hooks/useErrorAutoRetry";
import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";

/**
 * Error component for the app.
 *
 * @param error - The error object
 * @param reset - The function to reset the error
 */
export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  // Silently retry once for transient (e.g. cold-start) failures before showing the card.
  // Render a neutral, announced placeholder during the retry instead of a blank screen.
  const isRetrying = useErrorAutoRetry(error, reset);
  if (isRetrying) {
    return (
      <div role="status" aria-live="polite" className="flex min-h-screen items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  return (
    <ErrorCard
      title="Something went wrong"
      message="Don't worry! Our friendly pup is on the case. Try again or head back home while we fetch a solution."
      sparkleType="hearts"
      showBackButton={false}
      showHomeButton={true}
      showRetryButton={true}
      onRetry={reset}
      helpText="Still having issues?"
    />
  );
}
